<template lang="pug">
.page.page--light.podcasts
  error-layout(
    v-if="$fetchState.error"
    :error="$fetchState.error")

  template(v-else)
    section.page__header#intro
      .pd-container.framed
        el-row
          el-col(:md="24" v-once)
            img.page__logo(
              data-aos-anchor="#intro"
              data-aos="fade-down"
              src="/img/pod-podcast/logo.svg" width="391" height="89" alt="Pod Podcast")
            h1.page__title.pd-h3(
              data-aos-anchor="#intro"
              data-aos="fade-down")
              span Tune in for real talk&nbsp;
              br.hidden-md-and-up
              | with CPG founders.
              br
              small Hosted by&nbsp;
                br.hidden-sm-and-up
                i.hosted-by
                span Peter Gialantzis.

            el-row
              el-col(:xs="24" :md="16")
                .page__description(
                  data-aos-anchor="#intro"
                  data-aos="zoom-out") Want to be featured?
                  .page__actions
                    a.el-button.el-button--primary.is-round(
                      :href="`mailto:${CONTACT_EMAIL}`"
                      title="Contact us"
                      target="_blank") Email us
                    a.el-button.el-button--ghost.is-round(
                      :href="LINKEDIN_URL"
                      target="_blank") Connect on LinkedIn

              el-col(:xs="24" :md="8")
                .page__description.page__networks(
                  data-aos-anchor="#intro"
                  data-aos="zoom-out")
                    span Follow us on
                    a(:href="YOUTUBE_URL" target="_blank")
                      i.bx.bxl-youtube
                    a(:href="SPOTIFY_URL" target="_blank")
                      i.bx.bxl-spotify

    .pd-container.framed
      .theater
        .theater__player(ref="player")
          podcast-player(
            v-if="record || firstRecord"
            :entry="record || firstRecord"
            @close="close")

        .theater__playlist
          podcast-card(
            v-for="i, k in records"
            :key="k"
            :entry="i")

          .load-more.tc.mt-3(v-if="canFetchMore")
            el-button(
              :loading="fetching"
              :disabled="fetching"
              @click="fetchPodcasts") Load more

</template>

<script>
import { WEBSITE_BASE_URL, CONTACT_EMAIL, LINKEDIN_URL, YOUTUBE_URL, SPOTIFY_URL } from '~/settings'

import ErrorLayout from '~/layouts/error'
import PodcastCard from '~/components/podcasts/PodcastCard'
import PodcastPlayer from '~/components/podcasts/PodcastPlayer'

const PODCASTS_LIMIT = 12

export default {
  components: {
    ErrorLayout,
    PodcastCard,
    PodcastPlayer
  },

  layout: 'landing',

  data () {
    return {
      CONTACT_EMAIL,
      LINKEDIN_URL,
      YOUTUBE_URL,
      SPOTIFY_URL,
      title: 'Pod Podcast',
      records: [],
      record: null,
      page: 0,
      totalPages: 1,
      fetching: false
    }
  },

  fetch () {
    this.records = []
    const tasks = [
      this.fetchPodcast(),
      this.fetchPodcasts()
    ]
    return Promise
      .all(tasks)
      .catch(e => {
        const error = { statusCode: 503 }
        if (process.server) {
          this.$nuxt.context.res.statusCode = error.statusCode
          this.$fetchState.error = error
        } else {
          this.$nuxt.error(error)
        }
      })
  },

  head () {
    return this.$seo.head(
      this.record
        ? {
          title: this.record.title,
          description: this.record.summary,
          properties: {
            'og:image': this.record.thumbnail_product_url,
            'og:image:type': ''
          }
        }
        : {
          title: this.title,
          description: 'Tune in for real talk with CPG founders, hosted by Peter Gialantzis',
          properties: {
            'og:image': `${WEBSITE_BASE_URL}/img/opengraph/pod-podcast.jpg?pod`,
            'og:image:type': 'image/jpeg'
          }
        }
    )
  },

  computed: {
    firstRecord () {
      return this.records?.[0]
    },

    canFetchMore () {
      return this.page < this.totalPages
    },

    featured () {
      return this.records.find(i => i?.fields?.featured)
    },

    slug () {
      return this.$route.query.episode
    }
  },

  watch: {
    slug: {
      immediate: true,
      handler: 'open'
    }
  },

  methods: {
    open () {
      this.fetchPodcast()
      this.scrollUpOnMobile()
    },

    scrollUpOnMobile () {
      if (!process.client) return
      if (window?.innerWidth > 768) return
      window?.scrollTo({
        top: this.$refs.player?.offsetTop - 64,
        behavior: 'smooth'
      })
    },

    close () {
      this.record = null
      this.$router.push({
        query: {}
      })
    },

    fetchPodcast () {
      if (!this.slug) return
      const found = this.records.find(i => i.slug === this.slug)
      if (found) {
        this.record = found
        return Promise.resolve(found)
      }
      return this.$api.common
        .getPodcast(this.slug)
        .then(res => {
          this.record = res
        })
    },

    fetchPodcasts () {
      if (!this.canFetchMore) return
      this.fetching = true
      return this.$api.common
        .getPodcasts({
          sort: 'published',
          direction: 'desc',
          per_page: PODCASTS_LIMIT,
          page: this.page + 1
        })
        .then(res => {
          this.records = [
            ...this.records,
            ...res.results
          ]
          this.page = res.current_page
          this.totalPages = res.num_pages
        })
        .finally(() => {
          this.fetching = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.podcasts {
  .page {
    &__header {
      padding: 24px 0;
      text-align: center;
      @include media(md) {
        text-align: left;
      }
    }
    &__logo {
      width: auto;
      height: 32px;
      @include media(md) {
        height: 40px;
      }
    }
    &__title {
      > * {
        vertical-align: baseline;
      }
      i.hosted-by {
        display: inline-block;
        width: 1em;
        height: 1em;
        border-radius: 1em;
        background-color: $--color-placeholder;
        background-image: url(/img/pod-podcast/hosted-by.webp);
        background-size: cover;
        margin-right: .25em;
        margin-bottom: -.15em;
        line-height: 150%;
      }
    }
    &__actions {
      display: inline-flex;
      gap: 12px;
      margin-top: 12px;
      @include media(sm) {
        margin-top: 0;
        margin-left: 16px;
      }
      a {
        margin: 0;
      }
    }
    &__description {
      font-size: 16px;
    }
    &__networks {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: center;
      @include media(md) {
        justify-content: flex-end;
      }
      i {
        font-size: 40px;
        &.bxl-youtube {
          color: #FF0000;
        }
        &.bxl-spotify {
          color: #1DB954;
        }
      }
    }
    &__section {
      padding: 3rem 0;
      background: white;
      position: relative;
      z-index: 1;
      @include media(md) {
        padding: 4rem 0;
      }
    }
  }

  .theater {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media(md) {
      flex-direction: row;
      gap: 40px;
      margin-top: 40px;
    }
    > * {
      min-width: 0;
    }
    &__player {
      top: 96px;
      flex: 2;
      border-radius: 12px;
      overflow: hidden;
      height: fit-content;
      z-index: 1;
      @include media(md) {
        position: sticky;
      }
    }
    &__playlist {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 16px;
      > * {
        min-width: 0;
      }
    }
  }
}
</style>
